import { jsx, jsxs } from "react/jsx-runtime";
import { Typography, useThemeProps } from "@mui/material";
import { Children, useCallback, useLayoutEffect, useRef, useState } from "react";
import { Canary } from "./components/Canary.js";
import { Container } from "./components/Container.js";
import { Content } from "./components/Content.js";
import { Gradient } from "./components/Gradient.js";
import { More } from "./components/More.js";
import { Observe } from "./components/Observe.js";
import { useStyles } from "./hooks/useStyles.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
import { truncateListName } from "./classes.js";
function DefaultShowMore({ count }) {
  return /* @__PURE__ */ jsxs(Typography, { sx: { padding: "0px 10px" }, children: [
    "+ ",
    count,
    " more"
  ] });
}
const defaultShowMoreProps = {};
function TruncateList(inProps) {
  const props = useThemeProps({
    props: inProps,
    name: truncateListName
  });
  const {
    testid,
    density = "standard",
    children,
    onChange,
    multiline = false,
    threshold: userThreshold = 1,
    showGradient = false,
    showMoreProps = defaultShowMoreProps,
    showMoreColor = "#ffffff",
    gradientWidth = 50,
    ShowMore = DefaultShowMore,
    showChildrenOutsideViewport = false,
    onChangeHiddenItemsCount,
    listAlignment
  } = props;
  const threshold = userThreshold >= 0.01 && userThreshold <= 1 ? userThreshold : 1;
  const styles = useStyles({ gradientWidth, showMoreColor });
  const rOuterViewport = useRef(null);
  const rInnerViewport = useRef(null);
  const rCanaryIndex = useRef(Math.max(0, Children.count(children) - 1));
  useLayoutEffect(() => {
    rCanaryIndex.current = Children.count(children) - 1;
  }, [children]);
  const [canaryVisible, setCanaryVisible] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const handleOnChange = useCallback(
    (inView, entry) => {
      const index = Number.parseInt(entry.target.dataset.index ?? "-1");
      const ratio = entry.intersectionRatio ?? 0;
      if (index <= -1) return;
      const isCanary = index === rCanaryIndex.current;
      const isConsideredOutOfViewport = ratio < threshold;
      const childrenCount = rCanaryIndex.current + 1;
      if (isConsideredOutOfViewport) {
        setHiddenCount((prevCount) => {
          const nextCount = childrenCount - index;
          return prevCount > nextCount ? prevCount : nextCount;
        });
      } else if (isCanary) {
        setHiddenCount(0);
      } else {
        setHiddenCount(childrenCount - index - 1);
      }
      onChange?.(index, isConsideredOutOfViewport, entry);
    },
    [onChange, threshold]
  );
  const handleOnChangeCanary = useCallback((_, entry) => {
    const ratio = entry.intersectionRatio ?? 0;
    const visible = ratio >= 1;
    setCanaryVisible(visible);
  }, []);
  const rCanary = useRef(null);
  const rCanaryRect = useRef();
  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      const target = rCanary.current;
      const viewport = rOuterViewport.current;
      if (!target || !viewport) return;
      const viewportRect = viewport.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      rCanaryRect.current = {
        top: targetRect.top - viewportRect.top,
        left: targetRect.left - viewportRect.left,
        width: targetRect.width,
        height: targetRect.height
      };
    });
  });
  const hiddenCountValue = canaryVisible ? 0 : hiddenCount > 0 ? hiddenCount : rCanaryIndex.current > -1 ? 1 : 0;
  useLayoutEffect(() => {
    onChangeHiddenItemsCount?.(hiddenCount);
  }, [onChangeHiddenItemsCount, hiddenCount]);
  const showMore = canaryVisible ? false : hiddenCountValue > 0;
  const ownerState = {
    ...props
  };
  const utilityClasses = useUtilityClasses(ownerState);
  return /* @__PURE__ */ jsxs(
    Container,
    {
      ref: rOuterViewport,
      style: styles.root,
      "data-testid": testid,
      listAlignment,
      "data-tidal": "TruncateList",
      className: utilityClasses.root,
      children: [
        /* @__PURE__ */ jsxs(
          Content,
          {
            ref: rInnerViewport,
            density,
            multiline,
            className: utilityClasses.content,
            "data-testid": "truncate-list_inner",
            children: [
              Children.map(children, (child, idx) => {
                if (!child) return child;
                const isCanary = idx === rCanaryIndex.current;
                const ref = isCanary ? rCanary : void 0;
                const forceVisible = isCanary && canaryVisible;
                return /* @__PURE__ */ jsx(
                  Observe,
                  {
                    ref,
                    testid: `truncate_child_${idx}`,
                    index: idx,
                    onChange: handleOnChange,
                    viewport: rInnerViewport.current,
                    threshold,
                    forceVisible,
                    showChildrenOutsideViewport,
                    children: child
                  },
                  `truncate_child_${idx}`
                );
              }),
              !!showGradient && !!showMore && /* @__PURE__ */ jsx(Gradient, { "data-testid": "truncate-list_gradient" })
            ]
          }
        ),
        !!showMore && !!ShowMore && /* @__PURE__ */ jsx(More, { "data-testid": "truncate-list_show-more", children: /* @__PURE__ */ jsx(
          ShowMore,
          {
            extra: showMoreProps,
            count: hiddenCountValue,
            background: showMoreColor,
            gradientWidth
          }
        ) }),
        /* @__PURE__ */ jsx(Canary, { rect: rCanaryRect.current, onChange: handleOnChangeCanary, viewport: rOuterViewport.current })
      ]
    }
  );
}
export {
  TruncateList
};
//# sourceMappingURL=TruncateList.js.map
